import { useEffect, useState } from "react";
import { CalendarClock, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api } from "../../lib/api";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function TrackedCard({ item, onChange }) {
  const [editing, setEditing] = useState(false);
  const [startDate, setStartDate] = useState(item.start_date || todayISO());
  const [busy, setBusy] = useState(false);
  const ms = item.current_milestone;

  async function save() {
    setBusy(true);
    try {
      await api.post("/courses/track", { medication_id: item.medication_id, start_date: startDate });
      toast.success("Start date updated");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setBusy(false); }
  }

  async function stop() {
    if (!window.confirm(`Stop tracking ${item.medication_name}?`)) return;
    try {
      await api.delete(`/courses/track/${item.medication_id}`);
      onChange();
    } catch { toast.error("Couldn't stop tracking"); }
  }

  return (
    <div data-testid={`tracker-card-${item.medication_id}`} className="rounded-2xl border border-surface-line bg-white p-5 shadow-subtle">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">{item.medication_name}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-cream-100 border border-surface-line text-ink-soft font-semibold">
          {item.is_complete
            ? "Complete"
            : `Week ${item.current_week}${item.total_weeks ? ` of ${item.total_weeks}` : ""}`}
        </span>
      </div>
      {ms ? (
        <>
          <div data-testid={`tracker-week-title-${item.medication_id}`} className="mt-2 text-lg font-extrabold text-ink tracking-tight">
            Week {ms.week}: {ms.title}
          </div>
          {ms.body && <p className="mt-1 text-[14px] leading-relaxed text-ink-soft">{ms.body}</p>}
          {(ms.sources || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid={`tracker-sources-${item.medication_id}`}>
              {(ms.sources || []).map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[12px] text-safe-deep hover:underline">
                  {s.label} <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="mt-2 text-[14px] text-ink-soft">Your timeline starts soon — check back as the weeks progress.</p>
      )}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {editing ? (
          <>
            <input type="date" value={startDate} max={todayISO()} onChange={(e) => setStartDate(e.target.value)}
              data-testid={`tracker-date-${item.medication_id}`}
              className="h-9 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink" />
            <Button size="sm" onClick={save} disabled={busy} data-testid={`tracker-save-${item.medication_id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
            <button className="text-[12px] text-ink-mute hover:text-ink" onClick={() => setEditing(false)}>Cancel</button>
          </>
        ) : (
          <>
            <span className="text-[12px] text-ink-mute">Started {item.start_date}</span>
            <button className="text-[12px] text-ink-soft hover:text-ink underline" onClick={() => setEditing(true)}>Change date</button>
            <button className="text-[12px] text-ink-mute hover:text-avoid-deep ml-auto" onClick={stop}>Stop tracking</button>
          </>
        )}
      </div>
    </div>
  );
}

function UntrackedCard({ item, onChange }) {
  const [startDate, setStartDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    try {
      await api.post("/courses/track", { medication_id: item.medication_id, start_date: startDate });
      toast.success(`Tracking ${item.medication_name}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start tracking");
    } finally { setBusy(false); }
  }
  return (
    <div data-testid={`tracker-untracked-${item.medication_id}`} className="rounded-2xl border border-dashed border-surface-line bg-cream-100/40 p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">{item.medication_name}</div>
      <p className="mt-1 text-[14px] text-ink-soft">There's a week-by-week timeline for this. When did you start?</p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <input type="date" value={startDate} max={todayISO()} onChange={(e) => setStartDate(e.target.value)}
          data-testid={`tracker-start-date-${item.medication_id}`}
          className="h-9 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink" />
        <Button size="sm" onClick={start} disabled={busy} data-testid={`tracker-start-${item.medication_id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Start tracking"}
        </Button>
      </div>
    </div>
  );
}

export default function TreatmentTracker() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/courses/mine");
      setItems(data.items || []);
    } catch { /* silent — section just hides */ }
    finally { setLoaded(true); }
  }
  useEffect(() => { load(); }, []);

  // Nothing to show → render nothing (meds without an approved timeline).
  if (!loaded) return <p role="status">Loading treatment tracker…</p>;
  if (items.length === 0) return <div data-testid="treatment-tracker"><h2>Treatment tracker</h2><p className="family-empty">A timeline appears here when a medication saved to this profile has an approved treatment timeline.</p></div>;

  return (
    <div data-testid="treatment-tracker" className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock className="w-4 h-4 text-ink-soft" />
        <h2 className="text-lg font-bold text-ink tracking-tight">Treatment tracker</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => it.tracking
          ? <TrackedCard key={it.medication_id} item={it} onChange={load} />
          : <UntrackedCard key={it.medication_id} item={it} onChange={load} />)}
      </div>
    </div>
  );
}
